import React, { useState, useRef, useEffect } from 'react';
import { Play } from 'lucide-react';
import YouTubeModal from '@/components/youtube/YouTubeModal';
import { cn } from '@/lib/utils';

interface HomepageYouTubeHeroProps {
  videoId: string;
  title?: string;
  className?: string;
}

const HomepageYouTubeHero: React.FC<HomepageYouTubeHeroProps> = ({ 
  videoId, 
  title = "Featured Video",
  className 
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isIntersecting, setIsIntersecting] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const videoRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Check for reduced motion preference
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);

    const handleChange = (e: MediaQueryListEvent) => {
      setPrefersReducedMotion(e.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        // Only set to true when entering, never set back to false to prevent reloading
        if (entry.isIntersecting && !isIntersecting) {
          setIsIntersecting(true);
        }
      },
      { threshold: 0.3 }
    );

    if (videoRef.current) {
      observer.observe(videoRef.current);
    }

    return () => observer.disconnect();
  }, [isIntersecting]);

  const handleVideoClick = () => {
    setIsModalOpen(true);
  };

  return (
    <>
      <div className="w-full bg-black">
        <div className="h-[30px] bg-black"></div>
        <div 
          ref={videoRef}
          className={cn("relative w-full h-[40vh] md:h-[60vh] cursor-pointer group overflow-hidden", className)}
          onClick={handleVideoClick}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleVideoClick();
            }
          }}
          aria-label={`Play ${title}`}
        >
        {/* YouTube Embed - Only load when in view and motion is allowed */}
        {isIntersecting && !prefersReducedMotion ? (
          <iframe
            src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=0&showinfo=0&rel=0&iv_load_policy=3&modestbranding=1&start=0&end=30`}
            title={title}
            allow="autoplay; encrypted-media"
            className="absolute inset-0 w-full h-full object-cover pointer-events-none"
            style={{ border: 'none' }}
          />
        ) : (
          // Fallback poster with play button
          <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-primary/40 flex items-center justify-center">
            <div className="text-center text-white">
              <div className="mb-4 mx-auto w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center group-hover:bg-white/30 transition-colors">
                <Play size={32} className="ml-1" fill="currentColor" />
              </div>
              <h2 className="text-2xl font-bold mb-2">{title}</h2>
              <p className="text-white/80">Click to play</p>
            </div>
          </div>
        )}

        {/* Overlay on hover */}
        <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
          <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
            <Play size={32} className="ml-1 text-white" fill="currentColor" />
          </div>
        </div>
        </div>
      </div>

      <YouTubeModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        videoId={videoId}
        title={title}
      />
    </>
  );
};

export default HomepageYouTubeHero;